import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { User } from "@/api/types";
import { toast } from "@/components/ui/toast";
import { normalizePhone } from "@/lib/normalize-phone";
import { assertCurrentSession, sessionRevision } from "@/lib/session-request-scope";

interface RequestCodeInput {
  mobile_number: string;
}

interface RequestCodeResponse {
  dispatched?: boolean;
  already_verified?: boolean;
  destination: string;
  challenge_id?: string;
  user?: User;
  dev_code?: string;
}

interface VerifyCodeInput {
  mobile_number: string;
  code: string;
  challenge_id?: string;
}

interface VerifyCodeResponse {
  verified?: boolean;
  already_verified?: boolean;
  verified_at?: string;
  user: User;
}

export class PhoneConfirmationError extends Error {}

export function assertVerifiedPhone(user: User | undefined, phone: string): asserts user is User {
  if (!user?.phone_verified_at || user.mobile_number !== normalizePhone(phone)) {
    throw new PhoneConfirmationError(
      "Your new phone number was not verified. Request a new code and try again.",
    );
  }
}

export function useRequestAccountPhoneCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RequestCodeInput) => {
      const response = await api
        .post("users/me/phone/request_code", {
          json: { ...data, mobile_number: normalizePhone(data.mobile_number) },
        })
        .json<RequestCodeResponse>();
      if (response.already_verified) assertVerifiedPhone(response.user, data.mobile_number);
      return response;
    },
    onSuccess: async (data) => {
      const verifiedUser = data.user;
      if (data.already_verified && verifiedUser) {
        const revision = sessionRevision();
        await queryClient.cancelQueries({ queryKey: ["me"], exact: true });
        assertCurrentSession(revision);
        queryClient.setQueryData<User | null>(["me"], (user) =>
          user?.id === verifiedUser.id
            ? {
                ...user,
                mobile_number: verifiedUser.mobile_number,
                phone_verified_at: verifiedUser.phone_verified_at,
                available_reminder_channels: [
                  ...verifiedUser.available_reminder_channels.filter(
                    (channel) => channel === "sms",
                  ),
                  ...user.available_reminder_channels.filter((channel) => channel === "email"),
                ],
                reminder_channel_versions: {
                  ...user.reminder_channel_versions,
                  sms: verifiedUser.reminder_channel_versions.sms,
                },
              }
            : user,
        );
        void queryClient.invalidateQueries({ queryKey: ["bookings"] });
        void queryClient.invalidateQueries({ queryKey: ["booking"] });
      }
      if (data.dev_code) {
        toast({ variant: "info", title: `Dev code: ${data.dev_code}` });
      }
    },
  });
}

export function useVerifyAccountPhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: VerifyCodeInput) => {
      const response = await api
        .post("users/me/phone/verify", {
          json: { ...data, mobile_number: normalizePhone(data.mobile_number) },
        })
        .json<VerifyCodeResponse>();
      assertVerifiedPhone(response.user, data.mobile_number);
      return response;
    },
    onSuccess: async (data) => {
      const revision = sessionRevision();
      await queryClient.cancelQueries({ queryKey: ["me"], exact: true });
      assertCurrentSession(revision);
      queryClient.setQueryData<User | null>(["me"], (user) =>
        user?.id === data.user.id
          ? {
              ...user,
              mobile_number: data.user.mobile_number,
              phone_verified_at: data.user.phone_verified_at,
              available_reminder_channels: [
                ...data.user.available_reminder_channels.filter((channel) => channel === "sms"),
                ...user.available_reminder_channels.filter((channel) => channel === "email"),
              ],
              reminder_channel_versions: {
                ...user.reminder_channel_versions,
                sms: data.user.reminder_channel_versions.sms,
              },
            }
          : user,
      );
      void queryClient.invalidateQueries({ queryKey: ["bookings"] });
      void queryClient.invalidateQueries({ queryKey: ["booking"] });
      void queryClient.invalidateQueries({ queryKey: ["onboarding-status"] });
    },
  });
}
